<template>
  <div>
    <div class="product-page container">
      <grouped-products
        v-if="groupedProducts && groupedProducts.length"
        :grouped-products="groupedProducts"
        @newProduct="newProduct"
      />
      <bread-crumbs :items="breadCrumbs" />
      <v-content
        v-if="isLoading"
        class="product-page__loading"
        :no-side-padd="true"
      >
        <logo-loading />
      </v-content>
      <transition name="fade">
        <v-content v-if="!isLoading" :no-side-padd="true">
          <alert v-if="submitError" type="error" :message="submitError" />
          <product-editor
            v-if="product"
            :product="product"
            :product-stock="productStock"
            :gallery-items="galleryItems"
            @updateDeviceName="updateDeviceName"
            @displayPrice="displayPrice"
            @updateProductType="updateProductType"
            @updateDescription="updateDescription"
          >
            <template v-if="product && product.supports_institution">
              <div
                v-show="!showGoForGood && !goForGoodError"
                class="goforgood-loading"
              >
                <div style="margin: 0 auto">
                  <three-dots-loading />
                </div>
              </div>
              <div v-show="goForGoodError" class="goforgood-loading">
                <div style="margin: 0 auto">
                  <p>
                    {{ $t('go_for_good.fetch_error') }}
                    <a rel="nofollow" @click.prevent="fetchGoForGood">{{
                      $t('go_for_good.try_fetch')
                    }}</a>
                  </p>
                </div>
              </div>
              <campaing-widget
                v-show="showGoForGood && !goForGoodError"
                :commission-percentage="
                  goForGoodStructure.commission_percentage
                "
                :raised-amount="goForGoodStructure.raised_amount"
                :institution-name="goForGoodStructure.institution_name"
                @scrollToInstitution="scrollToInstitution"
              />
            </template>
          </product-editor>

          <lazy-hydrate
            v-slot="{ hydrated }"
            :trigger-hydration="$store.state.userInteracted"
          >
            <template v-if="hydrated && product.supports_institution">
              <div
                v-show="!showGoForGood && !goForGoodError"
                class="goforgood-tab-loading"
              >
                <div style="margin: 0 auto">
                  <three-dots-loading />
                </div>
              </div>
              <div v-show="goForGoodError" class="goforgood-tab-loading">
                <div style="margin: 0 auto">
                  <p>
                    {{ $t('go_for_good.fetch_error') }}
                    <a rel="nofollow" @click.prevent="fetchGoForGood">{{
                      $t('go_for_good.try_fetch')
                    }}</a>
                  </p>
                </div>
              </div>
              <tabs
                v-show="hydrated && showGoForGood && !goForGoodError"
                ref="institution"
                :institution-name="goForGoodStructure.institution_name"
                :about-institution="goForGoodStructure.about_institution"
                :about-go-for-good="goForGoodStructure.about_go_for_good"
                :image="goForGoodStructure.image"
              />
            </template>
          </lazy-hydrate>
        </v-content>
      </transition>
    </div>

    <span>
      <shelf
        v-for="(rr, idx) in richRelevances"
        :key="idx"
        :shelf-products="{ type: 'rich_relevance', ...rr }"
        :class="'shelf--white'"
      />
    </span>

    <lazy-hydrate
      v-slot="{ hydrated }"
      :trigger-hydration="$store.state.userInteracted"
    >
      <product-info-sections
        v-if="hydrated && product"
        :product="product"
        :product-description="
          productDescription || (product && product.description)
        "
      />
    </lazy-hydrate>

    <stampedio
      v-if="product"
      :product="product"
      :current-price="currentPrice"
      :product-type="productType"
    />

    <hero-banner
      v-if="$store.state.userInteracted"
      :product="product"
      :mobile="true"
      class="hero-banners--mobile"
    />

    <hero-banner
      v-if="$store.state.userInteracted"
      :product="product"
      class="hero-banners--desktop"
    />

    <brand-icons v-if="$store.state.userInteracted" />
  </div>
</template>
<script>
/* eslint-disable no-console */

import { mapGetters } from 'vuex'
import LazyHydrate from 'vue-lazy-hydration'
import BreadCrumbs from '~/components/BreadCrumbs.vue'
import Alert from '~/components/Alert.vue'
import isPresent from '~/utils/isPresent'
import VContent from '~/components/shared/v-content/VContent.vue'
import ProductEditor from '~/components/product/product-editor/ProductEditor.vue'
import ThreeDotsLoading from '~/components/loadings/three-dots'
import renderRichRelevance from '~/mixins/render-rich-relevance'
import Shelf from '~/components/pages/shelf/Shelf.vue'
import CampaingWidget from '~/components/product/go-for-good/CampaingWidget.vue'
import namelableDevice from '~/mixins/namelable-device'
import LogoLoading from '~/components/loadings/logo'

const GroupedProducts = () =>
  import(
    /* webpackChunkName: "grouped-products"  */ '~/components/product/grouped-products/GroupedProducts.vue'
  )

const Tabs = () =>
  import(
    /* webpackChunkName: "go-for-good-tabs"  */ '~/components/product/go-for-good/Tabs.vue'
  )

const Stampedio = () =>
  import(
    /* webpackChunkName:  "stampedio"  */ '@/components/product/stampedio/Stampedio.vue'
  )

const ProductInfoSections = () =>
  import(
    /* webpackChunkName: "product-info-sections"  */ '~/components/product/product-info-sections/ProductInfoSections.vue'
  )

const HeroBanner = () =>
  import(
    /* webpackChunkName: "hero-banner"  */ '~/components/product/hero-banner/HeroBanner.vue'
  )

const BrandIcons = () =>
  import(
    /* webpackChunkName: "brand-icons"  */ '~/components/brand-icons/BrandIcons.vue'
  )

export default {
  name: 'CaseDeviceProductPage',
  components: {
    BreadCrumbs,
    Stampedio,
    Alert,
    VContent,
    HeroBanner,
    Tabs,
    ThreeDotsLoading,
    Shelf,
    ProductEditor,
    CampaingWidget,
    BrandIcons,
    ProductInfoSections,
    LazyHydrate,
    GroupedProducts,
    LogoLoading,
  },
  mixins: [renderRichRelevance, namelableDevice],
  async fetch() {
    const { params } = this.$route
    const { slug } = params

    await this.setProductBySlug(slug)
  },
  data() {
    return {
      submitError: '',
      goForGoodStructure: {},
      showGoForGood: false,
      goForGoodError: false,
      richRelevances: [],
      product: null,
      productStock: null,
      currentPrice: null,
      productType: null,
      productDescription: null,
      deviceName: null,
      productList: [],
      isLoading: false,
      groupedProductsParent: [],
    }
  },
  computed: {
    ...mapGetters({
      galleryItems: 'product/galleryItems',
      userInfo: 'userInfo',
    }),
    isMobileFixedMockup() {
      return this.$device.isMobile && this.$route.name === 'slug-p-case_device'
    },
    breadCrumbs() {
      return this.product?.breadcrumbs ?? []
    },
    description() {
      const desc =
        // eslint-disable-next-line camelcase
        this.product?.meta_description || this.product?.description || ''

      return desc.slice(0, 100) + '...'
    },
    title() {
      return this.product?.name
    },
    groupedProducts() {
      if (this.$route.query.grouped_products) {
        return [
          {
            slug: 'iphone-samsung-phone-case-mystic-girl',
            image:
              'https://static-production.gocase.com.br/products/60101/product/mockup_zodiaco.jpg?1639954518',
            title: 'Mystic Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-pet-girl',
            image:
              'https://static-production.gocase.com.br/products/59880/product/pet-girl_preview_%281%29.jpg?1641508120',
            title: 'Pet Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-sports-girl',
            image:
              'https://static-production.gocase.com.br/products/59881/product/Prancheta_1_copiar_5.jpg?1635284110',
            title: 'Sports Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-garota-profissoes',
            image:
              'https://static-production.gocase.com.br/products/59879/product/Prancheta_1_copiar_3.jpg?1635284050',
            title: 'Professions Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-garota-universo-universe-girl',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-universo/deluxe-iphone11/mockup?stamp=uploads/designcustomization/5706/preview_en_shrine/66c28e00e8d6f1ec62b37e028e6891ec.png&expires=yes',
            title: 'Universe Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-traveler-girl',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-viagem/deluxe-iphone11/mockup?stamp=uploads/designcustomization/4384/preview_en_shrine/c94c07894f94c534030d2770161079bc.png&expires=yes',
            title: 'Traveler Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-garota-floral-prisma-floral-girl',
            image:
              'https://ik.imagekit.io/gocase/govinci/prisma-garota-floral/deluxe-iphone11/mockup?stamp=uploads/designcustomization/127/preview_en_shrine/ae51cc68fdcc470dd76efc007f10701b.png&expires=yes',
            title: 'Floral Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-avatar-girl',
            image:
              'https://static-production.gocase.com.br/products/60820/product/avataren.jpg?1655907847',
            title: 'Cartoon Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-girl-sunflowers',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-girassois/deluxe-iphone11/mockup?stamp=uploads/designcustomization/7727/preview_en_shrine/a58b7b89211b349308339595a3f3865f.png&expires=yes',
            title: 'Girl Sunflowers',
          },
          {
            slug: 'iphone-samsung-phone-case-airport-girl-clean',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-aeroporto-clean/deluxe-iphone11/mockup?stamp=uploads/designcustomization/7721/preview_en_shrine/945fa627a95793c35ca9e4cf0631316f.png&expires=yes',
            title: 'Airport Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-meu-look-do-dia',
            image:
              'https://ik.imagekit.io/gocase/govinci/meu-look-do-dia/deluxe-iphone11/mockup?stamp=prisma-render/prod/220684135883266521600891565893.png&expires=yes',
            title: 'Outfit Of The Day',
          },
          {
            slug: 'iphone-samsung-phone-case-garota-ballet',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-ballet/deluxe-iphone11/mockup?stamp=uploads/designcustomization/11117/preview_en_shrine/74635daef3975ebd1a72287fe6a13fdc.png&expires=yes',
            title: 'Ballet Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-beach-girl',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-praiana/deluxe-iphone11/mockup?stamp=uploads/designcustomization/10984/preview_en_shrine/fd7781a87fecd475a373c65b9e80cade.png&expires=yes',
            title: 'Beach Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-stardust-girl',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-poeira-das-estrelas/deluxe-iphone11/mockup?stamp=uploads/designcustomization/10725/preview_en_shrine/58c5e7793e3fa77279a65c440d05a9d0.png&expires=yes',
            title: 'Stardust Girl',
          },
          {
            slug: 'iphone-samsung-phone-case-garota-fitness',
            image:
              'https://ik.imagekit.io/gocase/govinci/garota-fitness/deluxe-iphone11/mockup?stamp=uploads/designcustomization/11563/preview_en_shrine/0f126fe06d002b5cabe9f0b4f821d313.png&expires=yes',
            title: 'Fitness Girl',
          },
        ]
      }
      // eslint-disable-next-line camelcase
      return this.groupedProductsParent?.grouped_products || []
    },
  },
  watch: {
    product() {
      this.$store.dispatch('product/setEngineLoaded', false)
      this.$store.dispatch('product/resetProductPrice')

      if (this.product) {
        this.fetchGoForGood()

        this.initRichRelevanceShelves([
          'item_page.click_cp1',
          'item_page.click_cp2',
        ])
      }
    },
    '$store.state.userInteracted'() {
      if (this.isMobileFixedMockup) {
        this.startMobileFixedMockup()
      }
    },
    groupedProductsParent(prod) {
      // eslint-disable-next-line camelcase
      const slug = prod?.grouped_products?.[0]?.slug
      if (slug) this.setProductBySlug(slug)
    },
  },
  mounted() {
    try {
      if (this.product) this.fetchGoForGood()

      if (this.$store.state.userInteracted && this.isMobileFixedMockup) {
        this.startMobileFixedMockup()
      }
    } catch (err) {
      this.$sentry.captureException(err)
    }
  },
  beforeDestroy() {
    this.nullifyProduct()
  },
  methods: {
    isPresent,
    nullifyProduct() {
      this.$store.commit('product/SET_PRODUCT', null)
      this.$store.commit('product/SET_PRODUCT_STOCK', null)
      this.$store.commit('CURRENT_CLICKED_PRODUCT_ID', null)
    },
    newProduct(slug) {
      this.setProductBySlug(slug)
    },
    async setProductBySlug(slug) {
      this.isLoading = true
      // this.nullifyProduct()

      const newProduct = this.productList.find(
        (prod) => prod.current_slug === slug
      )

      if (newProduct) {
        const params = {
          ...this.$store.getters.storeInfo,
        }
        const productStock = await this.$axios.$get(`/products/${slug}/stock`, {
          params,
        })
        this.product = newProduct
        this.$store.commit('product/SET_PRODUCT', newProduct)
        this.$store.commit('product/SET_PRODUCT_STOCK', productStock)

        this.isLoading = false
        return
      }

      const { query } = this.$route
      const { product, productStock } = await this.$store.dispatch(
        'product/fetchProduct',
        {
          slug,
          query,
        }
      )

      // eslint-disable-next-line camelcase
      if (!this.product && product?.grouped_products?.length) {
        this.groupedProductsParent = product
        return
      }

      product.current_slug = slug
      this.productList.push(product)
      this.product = product

      if (this.$route.query.customization_base) {
        this.product.customizations[0] = {
          ...this.product.customizations[0],
          line_item_customization: {
            value: this.$route.query.customization_base,
          },
        }
      }

      this.productStock = productStock

      this.isLoading = false
    },
    startMobileFixedMockup() {
      document.addEventListener('scroll', (e) => {
        if (this.$store.state.product.engineLoaded) {
          const largeImageContainer = document.querySelector(
            '.productpage__largeimage'
          )

          if (largeImageContainer) {
            largeImageContainer.style.opacity = 1
            largeImageContainer.style.transition = 'opacity 0.3s'
            const imageHeight = largeImageContainer.clientHeight

            if (
              window.pageYOffset >= (this.groupedProducts?.length ? 218 : 83)
            ) {
              largeImageContainer.style.position = 'fixed'
              largeImageContainer.style.top = '10px'
              largeImageContainer.style.boxShadow =
                '0px 1px 3px rgb(0 0 0 / 20%)'
              document.querySelector(
                '.productpage__details'
              ).style.marginTop = `${imageHeight}px`

              const productFormHeight = document.querySelector(
                '.productpage__form'
              ).clientHeight

              if (window.pageYOffset >= productFormHeight) {
                largeImageContainer.style.opacity = 0
                largeImageContainer.style.visibility = 'hidden'
              } else {
                largeImageContainer.style.opacity = 1
                largeImageContainer.style.visibility = 'visible'
              }
            } else {
              largeImageContainer.style.position = 'relative'
              document.querySelector('.productpage__details').style.marginTop =
                '0px'
              largeImageContainer.style.top = '0px'
              largeImageContainer.style.boxShadow = 'none'
            }
          }
        }
      })
    },
    updateDeviceName(deviceName) {
      this.deviceName = deviceName
    },
    updateProductType(productType) {
      this.productType = productType
    },
    updateDescription(description) {
      this.productDescription = description
    },
    displayPrice(price) {
      this.currentPrice = price
    },
    async fetchGoForGood() {
      try {
        this.goForGoodError = false
        this.showGoForGood = false
        // eslint-disable-next-line camelcase
        if (this.product?.supports_institution) {
          const params = {
            ...this.$store.getters.storeInfo,
            product_id: this.product.id,
          }
          this.goForGoodStructure = await this.$axios.$get(
            '/go_for_good/product_info',
            { params }
          )
          if (isPresent(this.goForGoodStructure)) this.showGoForGood = true
          this.goForGoodError = false
        }
      } catch (err) {
        this.goForGoodError = true
        this.$displayTopMessage(this.$t('action_error'), {
          type: 'error',
        })
      }
    },
    scrollToInstitution() {
      this.$refs.institution.$el.scrollIntoView({ behavior: 'smooth' })
    },
  },
  head() {
    const firstImage = this.galleryItems?.[0]?.url
    return {
      title: this.productNameWithDevice,
      meta: [
        {
          hid: 'keywords',
          name: 'keywords',
          // eslint-disable-next-line camelcase
          content: this.product?.meta_keywords,
        },
        {
          hid: 'description',
          name: 'description',
          // eslint-disable-next-line camelcase
          content: `${
            this.description
              ? this.description
              : this.$t('seo.meta_description')
          }${this.product?.name ? ' - ' + this.product?.name : ''}`,
        },
      ],
      link: firstImage
        ? [
            {
              rel: 'preload',
              as: 'image',
              href: firstImage,
            },
          ]
        : [],
    }
  },
}
</script>
<style lang="scss">
@import '~/assets/css/container.scss';
@import '~/assets/css/btn.scss';
@import '~/assets/css/_section.scss';
@import '~/assets/css/stamped.scss';

.stamped-fa-thumbs-down {
  display: none;
}

span.stamped-ugc-modal-vote-down:after {
  display: none;
}

.hero-banners--mobile {
  display: none;
}

.hero-banners--desktop {
  display: block;
}

@media (max-width: 650px) {
  .hero-banners--mobile {
    display: block;
  }

  .hero-banners--desktop {
    display: none;
  }
}

.product-page {
  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 50px auto;
    min-height: 60vh;
  }
  .goforgood-loading {
    min-height: 105px;
    display: flex;
    text-align: center;
    align-items: center;
  }
  .goforgood-tab-loading {
    min-height: 400px;
    display: flex;
    text-align: center;
    align-items: center;
  }
  @media (min-width: $screen_large) {
    .goforgood-loading {
      min-height: 77px;
      display: flex;
      text-align: center;
      align-items: center;
    }
  }
}
</style>
